<template>
  <div class="reception-page">
    <b-card no-body class="main-card">
      <b-card-body class="p-3">
        <!-- Action Cards -->
        <b-row>
          <b-col lg="6" sm="6" class="mb-3">
            <b-link :to="{ name: 'register-patient' }" class="nav-card-link">
              <b-card no-body class="nav-card">
                <div class="d-flex align-items-center nav-card-content">
                  <div class="nav-icon-wrapper">
                    <feather-icon
                      icon="UserPlusIcon"
                      size="20"
                      class="nav-icon"
                    />
                  </div>
                  <div class="nav-content">
                    <h6 class="mb-0 nav-title">
                      {{ $t("reception.add-patient") }}
                    </h6>
                  </div>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="16"
                    class="nav-arrow"
                  />
                </div>
              </b-card>
            </b-link>
          </b-col>
          <b-col lg="6" sm="6" class="mb-3">
            <b-link :to="{ name: 'Reception-Lobby' }" class="nav-card-link">
              <b-card no-body class="nav-card">
                <div class="d-flex align-items-center nav-card-content">
                  <div class="nav-icon-wrapper">
                    <feather-icon icon="UsersIcon" size="20" class="nav-icon" />
                  </div>
                  <div class="nav-content">
                    <h6 class="mb-0 nav-title">
                      {{ $t("reception.lobby") }}
                    </h6>
                  </div>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="16"
                    class="nav-arrow"
                  />
                </div>
              </b-card>
            </b-link>
          </b-col>
          <b-col lg="6" sm="6" class="mb-3">
            <b-link :to="{ name: 'Reception-Floors' }" class="nav-card-link">
              <b-card no-body class="nav-card">
                <div class="d-flex align-items-center nav-card-content">
                  <div class="nav-icon-wrapper">
                    <feather-icon
                      icon="LayersIcon"
                      size="20"
                      class="nav-icon"
                    />
                  </div>
                  <div class="nav-content">
                    <h6 class="mb-0 nav-title">
                      {{ $t("reception.floors") }}
                    </h6>
                  </div>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="16"
                    class="nav-arrow"
                  />
                </div>
              </b-card>
            </b-link>
          </b-col>
        </b-row>

        <!-- Search Section -->
        <b-card no-body class="search-card">
          <div class="compact-header">
            <div
              class="d-flex justify-content-between align-items-center flex-wrap"
            >
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="SearchIcon"
                  size="20"
                  class="mr-2 text-primary"
                />
                <h4 class="mb-0">
                  {{ $t("common.search") }}
                </h4>
              </div>
              <b-button-group size="sm" class="view-toggle">
                <b-button
                  :variant="
                    viewMode === 'cards' ? 'primary' : 'outline-primary'
                  "
                  @click="setViewMode('cards')"
                >
                  <feather-icon icon="GridIcon" size="14" class="mr-1" />
                  {{ $t("common.card_view") }}
                </b-button>
                <b-button
                  :variant="viewMode === 'list' ? 'primary' : 'outline-primary'"
                  @click="setViewMode('list')"
                >
                  <feather-icon icon="ListIcon" size="14" class="mr-1" />
                  {{ $t("common.list_view") }}
                </b-button>
              </b-button-group>
            </div>
          </div>
          <div class="compact-filters">
            <b-row class="g-2 align-items-end">
              <b-col md="4" cols="12">
                <b-form-group
                  :label="$t('reception.patient-name')"
                  label-for="patient-name"
                  class="mb-0"
                  label-size="sm"
                >
                  <b-form-input
                    id="patient-name"
                    v-model="patientName"
                    :placeholder="$t('reception.patient-name')"
                    size="sm"
                    @keydown.enter="searchPatients(true)"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" cols="12">
                <b-form-group
                  :label="$t('reception.patient-mother-name')"
                  label-for="mother-name"
                  class="mb-0"
                  label-size="sm"
                >
                  <b-form-input
                    id="mother-name"
                    v-model="patientMotherName"
                    :placeholder="$t('reception.patient-mother-name')"
                    size="sm"
                    @keydown.enter="searchPatients(true)"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4" cols="12">
                <b-button
                  variant="primary"
                  size="sm"
                  class="w-100"
                  @click="searchPatients(true)"
                >
                  <feather-icon icon="SearchIcon" size="14" class="mr-1" />
                  {{ $t("common.search") }}
                </b-button>
              </b-col>
            </b-row>
          </div>
        </b-card>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <b-spinner :label="$t('common.loading')" />
        </div>

        <!-- List View -->
        <b-card
          v-else-if="viewMode === 'list' && !loading"
          no-body
          class="data-card"
        >
          <b-table
            :items="patientsData"
            responsive
            :fields="tableColumns"
            primary-key="id"
            show-empty
            :empty-text="$t('common.no_results')"
            class="position-relative"
          >
            <template #cell(phone)="data">
              <span class="text-nowrap">
                {{ formatPhoneNumber(data.item.phone) }}
              </span>
            </template>
            <template #cell(actions)="data">
              <div class="text-nowrap d-flex">
                <b-button
                  class="button-preview"
                  pill
                  :variant="data.item.is_dead ? 'danger' : 'outline-secondary'"
                  @click="
                    $router.push({
                      name: 'Reception-patient-preview',
                      params: { id: data.item.id },
                    })
                  "
                >
                  {{ $t("common.preview") }}
                  <feather-icon
                    icon="EyeIcon"
                    size="16"
                    class="align-middle ml-1"
                  />
                </b-button>
                <b-button
                  class="button-preview"
                  pill
                  :variant="data.item.is_dead ? 'danger' : 'outline-secondary'"
                  @click="
                    $router.push({
                      name: 'patient-card-history',
                      params: { id: data.item.id },
                    })
                  "
                >
                  {{ $t("reception.patient-history") }}
                </b-button>
                <ActionsButtons
                  :no-edit="false"
                  :no-delete="true"
                  delete-perm="delete-patient"
                  edit-perm="update-patient"
                  @editToggle="editPatient(data.item)"
                  @deleteToggle="deleteModel(data.item)"
                />
              </div>
            </template>
          </b-table>

          <!-- Pagination -->
          <div class="mx-3">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">
                  {{ $t("common.showing") }} {{ (page - 1) * take + 1 }}
                  {{ $t("common.to") }}
                  {{ Math.min(page * take, pathientsNumber) }}
                  {{ $t("common.of") }} {{ pathientsNumber }}
                  {{ $t("common.patients") }}
                </span>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <b-pagination
                  v-model="page"
                  :total-rows="pathientsNumber"
                  :per-page="take"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-card>

        <!-- Card View -->
        <div v-else-if="viewMode === 'cards' && !loading">
          <b-row v-if="patientsData.length > 0">
            <b-col
              v-for="patient in patientsData"
              :key="patient.id"
              cols="12"
              md="6"
              lg="4"
              xl="3"
              class=""
            >
              <b-card no-body class="patient-card h-100">
                <div class="card-header-custom">
                  <div
                    class="d-flex align-items-center justify-content-between"
                  >
                    <div class="d-flex align-items-center">
                      <div class="patient-avatar">
                        <feather-icon
                          icon="UserIcon"
                          size="24"
                          :class="
                            patient.is_dead ? 'text-danger' : 'text-primary'
                          "
                        />
                      </div>
                      <h5 class="patient-name mb-0 ml-2">
                        {{ patient.name }}
                      </h5>
                    </div>
                    <!-- <b-badge
                      :variant="patient.is_dead ? 'danger' : 'success'"
                      pill
                      class="status-badge"
                    >
                      {{ patient.is_dead ? "Dead" : "Alive" }}
                    </b-badge> -->
                  </div>
                </div>

                <b-card-body class="card-body-custom">
                  <div class="patient-info-section">
                    <div class="info-item">
                      <feather-icon
                        icon="PhoneIcon"
                        size="16"
                        class="info-icon"
                      />
                      <span class="info-text">{{
                        patient.phone ? formatPhoneNumber(patient.phone) : "N/A"
                      }}</span>
                    </div>
                    <div class="info-item">
                      <feather-icon
                        icon="CalendarIcon"
                        size="16"
                        class="info-icon"
                      />
                      <span class="info-text">{{
                        patient.birth_date || "N/A"
                      }}</span>
                    </div>
                    <div class="info-item">
                      <feather-icon
                        icon="UserIcon"
                        size="16"
                        class="info-icon"
                      />
                      <span class="info-text">{{
                        patient.mother_name || "N/A"
                      }}</span>
                    </div>
                    <div v-if="patient.patient_no" class="info-item">
                      <feather-icon
                        icon="HashIcon"
                        size="16"
                        class="info-icon"
                      />
                      <span class="info-text">{{ patient.patient_no }}</span>
                    </div>
                  </div>
                </b-card-body>

                <div class="card-footer-custom">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    class="action-btn"
                    @click="
                      $router.push({
                        name: 'Reception-patient-preview',
                        params: { id: patient.id },
                      })
                    "
                  >
                    <feather-icon icon="EyeIcon" size="14" class="mr-1" />
                    {{ $t("common.preview") }}
                  </b-button>
                  <b-button
                    size="sm"
                    variant="outline-secondary"
                    class="action-btn"
                    @click="
                      $router.push({
                        name: 'patient-card-history',
                        params: { id: patient.id },
                      })
                    "
                  >
                    <feather-icon icon="ClockIcon" size="14" class="mr-1" />
                    {{ $t("reception.patient-history") }}
                  </b-button>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-card v-else no-body class="empty-state-card">
            <div class="text-center py-5">
              <feather-icon
                icon="SearchIcon"
                size="48"
                class="text-muted mb-2"
              />
              <p class="text-muted">
                {{ $t("common.no_results") }}
              </p>
            </div>
          </b-card>

          <!-- Pagination -->
          <div v-if="patientsData.length > 0" class="mt-4">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">
                  {{ $t("common.showing") }} {{ (page - 1) * take + 1 }}
                  {{ $t("common.to") }}
                  {{ Math.min(page * take, pathientsNumber) }}
                  {{ $t("common.of") }} {{ pathientsNumber }}
                  {{ $t("common.patients") }}
                </span>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <b-pagination
                  v-model="page"
                  :total-rows="pathientsNumber"
                  :per-page="take"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Delete Modal -->
    <DeleteModal modal-id="modal-delete" @deleteToggle="deleteUser" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BLink,
  BButton,
  BTable,
  BFormInput,
  BFormGroup,
  BPagination,
  BSpinner,
  BButtonGroup,
  BBadge,
} from "bootstrap-vue";
import ActionsButtons from "@/components/common/ActionsButtons.vue";
import DeleteModal from "@/components/modals/DeleteModal.vue";
import formatPhoneNumber from "@/utils/formatPhoneNumber";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BLink,
    BButton,
    BTable,
    BFormInput,
    BFormGroup,
    BPagination,
    BSpinner,
    BButtonGroup,
    BBadge,
    ActionsButtons,
    DeleteModal,
  },
  data() {
    return {
      viewMode: "cards",
      patientName: "",
      patientMotherName: "",
      loading: true,
      patientsData: [],
      pathientsNumber: 0,
      page: 1,
      take: 12,
      deleteForm: {},
      tableColumns: [
        { key: "name", label: this.$t("common.name"), sortable: true },
        {
          key: "patient_no",
          label: this.$t("reception.patient-no"),
          sortable: true,
        },
        {
          key: "birth_date",
          label: this.$t("reception.birth-date"),
          sortable: true,
        },
        {
          key: "phone",
          label: this.$t("reception.phone"),
          sortable: true,
          class: "text-nowrap",
        },
        {
          key: "mother_name",
          label: this.$t("reception.patient-mother-name"),
          sortable: true,
        },
        { key: "actions", label: this.$t("common.actions") },
      ],
    };
  },
  watch: {
    page(newPage, oldPage) {
      // Only trigger search if page actually changed (not initial load)
      // Skip if oldPage is undefined (initial load) or if we're currently loading
      if (oldPage !== undefined && newPage !== oldPage && !this.loading) {
        this.searchPatients(false);
      }
    },
  },
  mounted() {
    // Load view mode from localStorage
    const savedViewMode = localStorage.getItem("reception-view-mode");
    if (savedViewMode) {
      this.viewMode = savedViewMode;
    }
    this.searchPatients();
  },
  methods: {
    formatPhoneNumber,
    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem("reception-view-mode", mode);
    },
    async searchPatients(resetPage = false) {
      this.loading = true;
      if (resetPage) {
        this.page = 1;
      }
      let url = `/patient?page=${this.page}&take=${this.take}&sort=-id`;

      if (this.patientName) {
        url += `&filter[name]=${this.patientName}`;
      }
      if (this.patientMotherName) {
        url += `&filter[mother_name]=${this.patientMotherName}`;
      }

      try {
        await this.getList(url);
        this.pathientsNumber = this.all_data.total || 0;
        this.patientsData = this.all_data.data || [];
      } catch (error) {
        console.error("Error fetching patients:", error);
        this.patientsData = [];
        this.pathientsNumber = 0;
      } finally {
        this.loading = false;
      }
    },
    editPatient(item) {
      this.$router.push({
        name: "register-patient",
        params: { id: item.id },
      });
    },
    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show("modal-delete");
    },
    deleteUser() {
      this.$store
        .dispatch("delete", `/patient/${this.deleteForm.id}`)
        .then(() => {
          this.searchPatients();
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.deleted-successfully"),
              icon: "CheckIcon",
              variant: "success",
              text: `${this.deleteForm.name} ${this.$t(
                "common.deleted-successfully"
              )}`,
            },
          });
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.error"),
              variant: "danger",
              text: `${this.$t("common.error-deleting")} ${
                this.deleteForm.name
              }`,
            },
          });
        });
    },
  },
};
</script>

<style scoped lang="scss">
.reception-page {
  padding: 1rem;
}

.main-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

/* Navigation Cards */
.nav-card-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.nav-card {
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  background-color: #ffffff;
}

.nav-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #d1d5db;
}

.nav-card-content {
  padding: 0.625rem 0.875rem;
}

.nav-icon-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.nav-icon {
  color: #ffffff;
}

.nav-content {
  flex: 1;
}

.nav-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
}

.nav-arrow {
  color: #9ca3af;
  margin-left: auto;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.nav-card:hover .nav-arrow {
  transform: translateX(4px);
  color: #667eea;
}

.search-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.compact-header {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
}

.compact-filters {
  padding: 0.75rem 1rem;
}

.compact-filters ::v-deep .form-group {
  margin-bottom: 0;
}

.compact-filters ::v-deep label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.375rem;
}

.view-toggle {
  margin-top: 0.25rem;
}

.data-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

/* Patient Card Styles */
.patient-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  overflow: hidden;
}

.patient-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: #d1d5db;
}

.card-header-custom {
  background-color: #ffffff;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.patient-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.status-badge {
  font-size: 0.6875rem;
  padding: 0.25rem 0.625rem;
  font-weight: 500;
}

.card-body-custom {
  padding: 1rem;
}

.patient-name {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
}

.patient-info-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  background-color: #f9fafb;
  border-radius: 6px;
  transition: all 0.15s ease;
  border: 1px solid transparent;
}

.info-item:hover {
  background-color: #f3f4f6;
  border-color: #e5e7eb;
}

.info-icon {
  color: #6b7280;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.info-text {
  font-size: 0.8125rem;
  color: #374151;
  font-weight: 500;
  line-height: 1.4;
}

.card-footer-custom {
  padding: 0.75rem 1rem;
  background-color: #fafafa;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  flex: 1;
  min-width: 100px;
  font-size: 0.8125rem;
  font-weight: 500;
  transition: all 0.15s ease;
  padding: 0.375rem 0.75rem;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.empty-state-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

.button-preview {
  align-items: center;
  appearance: none;
  background-color: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 0.375em;
  box-shadow: none;
  box-sizing: border-box;
  color: #363636;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.875rem;
  height: 2.25em;
  justify-content: center;
  line-height: 1.5;
  padding: calc(0.5em - 1px) 1em;
  position: relative;
  text-align: center;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  vertical-align: top;
  white-space: nowrap;
  margin-bottom: 3px;
  margin-right: 10px;
  transition: all 0.2s ease;
}

.button-preview:hover {
  border-color: #667eea;
  background: #f0f4ff;
  transform: translateY(-1px);
}

.button-preview:active {
  border-color: #4a4a4a;
  outline: 0;
}

.button-preview:focus {
  border-color: #485fc7;
  outline: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .compact-header {
    padding: 0.625rem 0.875rem;
  }

  .compact-filters {
    padding: 0.625rem 0.875rem;
  }

  .view-toggle {
    width: 100%;
    margin-top: 0.5rem;
  }

  .view-toggle .btn {
    flex: 1;
  }

  .card-footer-custom {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
  }

  .patient-card {
    margin-bottom: 1rem;
  }

  .patient-name {
    font-size: 0.9375rem;
  }

  .nav-icon-wrapper {
    width: 32px;
    height: 32px;
    margin-right: 0.625rem;
  }

  .nav-title {
    font-size: 0.8125rem;
  }

  .nav-card-content {
    padding: 0.5rem 0.75rem;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
